/**
 * Raise an invoice from the ticked rows.
 *
 * It shows what is about to be billed before it bills it: the summary the
 * document will carry, grouped exactly as the document groups it. Everything
 * else on this screen — who it goes to, the period in the page header, the
 * "Re:" line — is a decision about the covering page, not about the work, and
 * defaults from the bill-to record so the usual case is two clicks.
 *
 * The address block and the bank details are not here at all. They come from
 * Settings and are copied onto the invoice as it is raised.
 */

import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'

import type { BillTo, WorkDay } from '@/api/types'
import { ConsultingSummary } from '@/components/ConsultingSummary'
import { Field } from '@/components/ui'
import { formatMoney, todayIso, trimHours } from '@/lib/consulting'
import { pluralize } from '@/lib/format'

interface DialogProps {
  open: boolean
  days: WorkDay[]
  billTos: BillTo[]
  onClose: () => void
  onCreate: (payload: {
    day_ids: number[]
    bill_to_id: number
    invoice_date: string
    period_label: string
    re_line: string | null
  }) => Promise<void>
}

/**
 * The default "For period ..." text.
 *
 * One month is named; a span across months is written out end to end, because
 * "August 2026" on an invoice covering half of September is a claim about what
 * is being billed and it would be false. Mirrors `period_label_for` on the
 * server, which is what actually fills this in when the field is left blank.
 */
function defaultPeriod(days: WorkDay[]): string {
  if (days.length === 0) return ''
  const dates = days.map((day) => day.work_date).sort()
  const parse = (iso: string) => {
    const [year, month, day] = iso.split('-').map(Number)
    return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1)
  }
  const first = parse(dates[0]!)
  const last = parse(dates[dates.length - 1]!)
  const monthName = (value: Date) => value.toLocaleDateString(undefined, { month: 'long' })

  if (first.getFullYear() === last.getFullYear() && first.getMonth() === last.getMonth()) {
    return `${monthName(first)} ${first.getFullYear()}`
  }
  if (first.getFullYear() === last.getFullYear()) {
    return `${monthName(first)} ${first.getDate()} - ${monthName(last)} ${last.getDate()}, ${last.getFullYear()}`
  }
  return (
    `${monthName(first)} ${first.getDate()}, ${first.getFullYear()} - ` +
    `${monthName(last)} ${last.getDate()}, ${last.getFullYear()}`
  )
}

/** The Summary table the document will carry, folded the same way it folds. */
function summarise(days: WorkDay[]) {
  const buckets = new Map<
    string,
    { client_name: string; rate_kind: WorkDay['rate_kind']; rate: number | null; hours: number }
  >()
  for (const day of days) {
    const key = `${day.client_name}|${day.rate_kind}|${day.rate ?? 'jv'}`
    const found = buckets.get(key)
    if (found) found.hours += day.hours
    else
      buckets.set(key, {
        client_name: day.client_name,
        rate_kind: day.rate_kind,
        rate: day.rate,
        hours: day.hours,
      })
  }
  const rows = [...buckets.values()]
    .sort(
      (a, b) =>
        Number(a.rate_kind === 'joint_venture') - Number(b.rate_kind === 'joint_venture') ||
        a.client_name.toLowerCase().localeCompare(b.client_name.toLowerCase()) ||
        (a.rate ?? 0) - (b.rate ?? 0),
    )
    .map((row) => ({
      ...row,
      hours: Number(row.hours.toFixed(2)),
      amount: row.rate_kind === 'joint_venture' || row.rate === null ? null : row.hours * row.rate,
    }))

  return {
    rows,
    hours: Number(rows.reduce((sum, row) => sum + row.hours, 0).toFixed(2)),
    amount: Number(rows.reduce((sum, row) => sum + (row.amount ?? 0), 0).toFixed(2)),
  }
}

export function ConsultingInvoiceDialog({
  open,
  days,
  billTos,
  onClose,
  onCreate,
}: DialogProps) {
  const [billToId, setBillToId] = useState<number | ''>('')
  const [invoiceDate, setInvoiceDate] = useState(todayIso())
  const [period, setPeriod] = useState('')
  const [reLine, setReLine] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const dialogRef = useRef<HTMLDivElement>(null)

  const totals = useMemo(() => summarise(days), [days])

  // Reset on open so last month's half-filled form never reappears. The
  // bill-to defaults to the one most of the selected clients already point at.
  useEffect(() => {
    if (!open) return
    const preferred = billTos[0]?.id ?? ''
    setBillToId(preferred)
    setInvoiceDate(todayIso())
    setPeriod(defaultPeriod(days))
    setReLine(billTos.find((entry) => entry.id === preferred)?.re_line ?? '')
    setError(null)
  }, [open, days, billTos])

  useEffect(() => {
    if (!open) return
    function onKey(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      event.preventDefault()
      onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  function chooseBillTo(value: number | '') {
    setBillToId(value)
    // The Re: line belongs to the payer, so switching payer brings theirs --
    // unless something has already been typed over it.
    const next = billTos.find((entry) => entry.id === value)
    if (next && !reLine.trim()) setReLine(next.re_line ?? '')
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (busy) return
    if (billToId === '') {
      setError('Choose who this is billed to.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await onCreate({
        day_ids: days.map((day) => day.id),
        bill_to_id: billToId,
        invoice_date: invoiceDate,
        period_label: period.trim() || defaultPeriod(days),
        re_line: reLine.trim() || null,
      })
      onClose()
    } catch {
      setError('Could not raise that invoice.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="invoice-dialog-title"
        className="card max-h-[90dvh] w-full max-w-2xl overflow-y-auto p-4 shadow-xl"
      >
        <h2 id="invoice-dialog-title" className="text-lg font-bold">
          Create an invoice
        </h2>
        <p className="mt-0.5 text-xs text-ink-3">
          {days.length} {pluralize(days.length, 'row')} &middot; {trimHours(totals.hours)}{' '}
          {pluralize(totals.hours, 'hour')}
          &middot; {formatMoney(totals.amount)}. These rows move to the archive; you can release
          any of them again later.
        </p>

        <form onSubmit={submit} className="mt-3 flex flex-col gap-3">
          <Field label="Bill to">
            <select
              className="field"
              value={billToId}
              onChange={(event) =>
                chooseBillTo(event.target.value ? Number(event.target.value) : '')
              }
            >
              <option value="">Choose...</option>
              {billTos.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Invoice date">
              <input
                type="date"
                className="field"
                value={invoiceDate}
                onChange={(event) => setInvoiceDate(event.target.value)}
              />
            </Field>
            <Field label="For period">
              <input
                className="field"
                value={period}
                onChange={(event) => setPeriod(event.target.value)}
                placeholder={defaultPeriod(days)}
              />
            </Field>
          </div>

          <Field label="Re:">
            <input
              className="field"
              value={reLine}
              onChange={(event) => setReLine(event.target.value)}
              placeholder="What the engagement is"
            />
          </Field>

          <ConsultingSummary
            rows={totals.rows}
            totalHours={totals.hours}
            totalAmount={totals.amount}
            caption="what the document will say"
          />

          {billTos.length === 0 && (
            <p className="text-sm text-error-text">
              There is nowhere to bill this yet. Add a bill-to address under Clients first.
            </p>
          )}
          {error && <p className="text-sm text-error-text">{error}</p>}

          <div className="flex justify-end gap-2">
            <button type="button" className="btn btn-neutral" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy || billToId === ''}>
              {busy ? 'Creating...' : 'Create invoice'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
